import { EMPLOYMENT_TYPE_LABELS, WORK_MODE_LABELS } from '@yeshuman/sdk'
/** Shared labels + one-line summaries for structured job posting fields (employer + cards). */


export { EMPLOYMENT_TYPE_LABELS, WORK_MODE_LABELS }

export type EmploymentType =
  | 'full_time'
  | 'part_time'
  | 'casual'
  | 'contract'
  | 'temporary'
  | 'internship'
  | ''

export type WorkMode = 'onsite' | 'hybrid' | 'remote' | ''

export type SalaryVisibility = 'shown' | 'not_disclosed'

export type SalaryPeriod = 'year' | 'month' | 'day' | 'hour' | ''

export const SALARY_PERIOD_LABELS: Record<string, string> = {
  year: ' / year',
  month: ' / month',
  day: ' / day',
  hour: ' / hour',
}

const salaryNumberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })

/**
 * Map API/legacy strings onto canonical keys so Radix `Select` `value` matches a `SelectItem`.
 * Handles extra whitespace, `Full time` vs `full_time`, etc.
 */
export function normalizeSelectEnumKey(
  raw: string | null | undefined,
  labels: Record<string, string>,
): string {
  const s = (raw ?? '').trim()
  if (!s) return ''
  if (s in labels) return s
  const underscored = s.toLowerCase().replace(/[\s-]+/g, '_')
  if (underscored in labels) return underscored
  for (const [key, label] of Object.entries(labels)) {
    if (label.toLowerCase() === s.toLowerCase()) return key
    if (key.toLowerCase() === s.toLowerCase()) return key
  }
  return ''
}

export function normalizeSalaryPeriodKey(raw: string | null | undefined): string {
  const s = (raw ?? '').trim().toLowerCase()
  if (s && s in SALARY_PERIOD_LABELS) return s
  return ''
}

export type OpportunityJobSummaryInput = {
  employment_type?: string | null
  work_mode?: string | null
  /** Legacy / derived display string when structured fields aren’t filled */
  location?: string | null
  city?: string | null
  country_code?: string | null
  salary_visibility?: string | null
  salary_min?: number | null
  salary_max?: number | null
  salary_currency?: string | null
  salary_period?: string | null
}

/** API opportunity rows share this field set for list cards and detail meta. */
export type OpportunityJobMetaSource = Pick<
  OpportunityJobSummaryInput,
  | 'employment_type'
  | 'work_mode'
  | 'location'
  | 'city'
  | 'country_code'
  | 'salary_visibility'
  | 'salary_min'
  | 'salary_max'
  | 'salary_currency'
  | 'salary_period'
>

export function toOpportunityJobSummaryInput(
  source: OpportunityJobMetaSource,
): OpportunityJobSummaryInput {
  return { ...source }
}

/** e.g. `EUR 10 – 15 / hour` when salary is shown and complete. */
export function formatOpportunitySalaryRange(input: OpportunityJobSummaryInput): string | null {
  if ((input.salary_visibility || 'not_disclosed').trim() !== 'shown') return null
  const cur = (input.salary_currency || '').trim().toUpperCase()
  const mn = input.salary_min
  const mx = input.salary_max
  const period =
    normalizeSalaryPeriodKey(input.salary_period) || (input.salary_period || '').trim()
  if (!cur || mn == null || mx == null || !period || !SALARY_PERIOD_LABELS[period]) return null
  return `${cur} ${salaryNumberFormatter.format(mn)} – ${salaryNumberFormatter.format(mx)}${SALARY_PERIOD_LABELS[period]}`
}

/** Structured city + ISO country, else legacy free-text location (excluding "remote"). */
export function formatOpportunityJobPlace(
  input: Pick<OpportunityJobSummaryInput, 'city' | 'country_code' | 'location'>,
): string {
  const city = (input.city || '').trim()
  const cc = (input.country_code || '').trim().toUpperCase()
  if (city) {
    return cc ? `${city}, ${cc}` : city
  }
  const loc = (input.location || '').trim()
  if (loc && loc.toLowerCase() !== 'remote') {
    return loc
  }
  return ''
}

/** Short line for lists and previews: `Full time · Hybrid · Sydney, AU · Salary …`. */
export function formatOpportunityJobSummary(input: OpportunityJobSummaryInput): string | null {
  const parts: string[] = []
  const et = (input.employment_type || '').trim()
  if (et && EMPLOYMENT_TYPE_LABELS[et]) {
    parts.push(EMPLOYMENT_TYPE_LABELS[et])
  }
  const wm = (input.work_mode || '').trim()
  if (wm && WORK_MODE_LABELS[wm]) {
    parts.push(WORK_MODE_LABELS[wm])
  }
  const place = formatOpportunityJobPlace(input)
  if (place) {
    parts.push(place)
  }

  const vis = (input.salary_visibility || 'not_disclosed').trim()
  if (vis === 'shown') {
    const salary = formatOpportunitySalaryRange(input)
    if (salary) parts.push(salary)
  }

  return parts.length > 0 ? parts.join(' · ') : null
}
